import { useState, useEffect, useCallback, useRef } from "react";
import { SortField, SortDirection } from "../types/GpuListing";
import { FilterParams } from "./useListings";

interface UseFiltersProps {
  availableManufacturers: string[];
  availableRetailers: string[];
  gpuCard: string;
  onFilterChange: (filters: FilterParams) => void;
}

export const useFilters = ({
  availableManufacturers,
  availableRetailers,
  gpuCard,
  onFilterChange,
}: UseFiltersProps) => {
  const [retailerFilter, setRetailerFilter] = useState<string[]>([]);
  const [manufacturerFilter, setManufacturerFilter] = useState<string[]>([]);
  const [stockFilter, setStockFilter] = useState<string[]>([
    "in_stock",
    "incoming",
    "out_of_stock",
    "unknown",
  ]);
  const [modelSearch, setModelSearch] = useState<string>("");
  const [productSearch, setProductSearch] = useState<string>("");
  const [sortField, setSortField] = useState<SortField>("price");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const isInitialMount = useRef(true);
  const prevGpuCard = useRef(gpuCard);

  // Reset all filters when card changes
  useEffect(() => {
    if (prevGpuCard.current !== gpuCard) {
      setManufacturerFilter([]);
      setRetailerFilter([]);
      setStockFilter(["in_stock", "incoming", "out_of_stock", "unknown"]);
      setModelSearch("");
      setProductSearch("");
      prevGpuCard.current = gpuCard;
    }
  }, [gpuCard]);

  // Build current filter params
  const getFilterParams = useCallback((): FilterParams => {
    const params: FilterParams = {
      sortField,
      sortDirection,
    };

    // Only include filters if they're not "all selected"
    if (manufacturerFilter.length > 0 && manufacturerFilter.length < availableManufacturers.length) {
      params.manufacturers = manufacturerFilter;
    }
    if (retailerFilter.length > 0 && retailerFilter.length < availableRetailers.length) {
      params.retailers = retailerFilter;
    }
    if (stockFilter.length > 0 && stockFilter.length < 4) {
      params.stock = stockFilter;
    }
    if (modelSearch.trim()) {
      params.modelSearch = modelSearch.trim();
    }
    if (productSearch.trim()) {
      params.productSearch = productSearch.trim();
    }

    return params;
  }, [
    manufacturerFilter,
    retailerFilter,
    stockFilter,
    modelSearch,
    productSearch,
    sortField,
    sortDirection,
    availableManufacturers.length,
    availableRetailers.length,
  ]);

  // Trigger API call when filters change (but not on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    onFilterChange(getFilterParams());
  }, [
    manufacturerFilter,
    retailerFilter,
    stockFilter,
    modelSearch,
    productSearch,
    sortField,
    sortDirection,
    getFilterParams,
    onFilterChange,
  ]);

  const handleSort = useCallback((field: SortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  }, [sortField]);

  return {
    retailerFilter,
    setRetailerFilter,
    manufacturerFilter,
    setManufacturerFilter,
    stockFilter,
    setStockFilter,
    modelSearch,
    setModelSearch,
    productSearch,
    setProductSearch,
    sortField,
    sortDirection,
    handleSort,
    uniqueRetailers: availableRetailers,
    uniqueManufacturers: availableManufacturers,
  };
};
